import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePublishToEmma } from '../hooks/usePublishToEmma';
import portalT from '../translations/portal';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import ExportPDFButton from '../components/ExportPDFButton';
import {
  ArrowLeft, FlaskConical, Send, Loader, Lightbulb, TrendingUp,
  Users, Megaphone, Package, Scissors, Globe, Plus,
  ArrowRight, Shield, Target, MessageSquare, ChevronDown, ChevronUp,
  Save, BookOpen, GitCompare, Trash2, Check,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const PRESET_ICONS = {
  'trending-up': TrendingUp, users: Users, megaphone: Megaphone,
  package: Package, scissors: Scissors, globe: Globe,
};

const ScenarioSimulator = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { lang } = useLanguage();
  const t = portalT[lang];
  const tp = t.toolPages.simulator;
  const chatEndRef = useRef(null);

  const [view, setView] = useState('setup'); // setup | result | saved | compare
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [metrics, setMetrics] = useState({});
  const [customQuestion, setCustomQuestion] = useState('');

  const [simulating, setSimulating] = useState(false);
  const [simData, setSimData] = useState(null);
  const [analysisText, setAnalysisText] = useState('');
  const [sessionId, setSessionId] = useState(null);

  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [sending, setSending] = useState(false);
  const [showFullAnalysis, setShowFullAnalysis] = useState(false);

  // Save/compare state
  const [savedSims, setSavedSims] = useState([]);
  const [saveTitle, setSaveTitle] = useState('');
  const [saveNotes, setSaveNotes] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [compareSelected, setCompareSelected] = useState([]);
  const [compareResult, setCompareResult] = useState(null);
  const [comparing, setComparing] = useState(false);
  
  // Quick decision state
  const [quickQuestion, setQuickQuestion] = useState('');

  
  const lastAssistant = (Array.isArray(messages) ? messages : []).filter(m => m.role === 'assistant').slice(-1)[0]?.content;
  usePublishToEmma('AI Decision Simulator', lastAssistant);
  const headers = { Authorization: `Bearer ${token}` };

  const txt = {
    en: {
      pageTitle: 'AI Decision Simulator',
      pageSubtitle: 'Test business decisions safely before you act',
      quickQuestion: 'Quick Decision',
      quickQuestionDesc: 'Just ask your question directly - no numbers needed',
      quickPlaceholder: 'E.g.: Should I hire another employee? Should I raise my prices 10%?',
      askBtn: 'Get AI Advice',
      orChoose: '— or choose a detailed scenario —',
      chooseScenario: 'What business decision do you want to test?',
      chooseDesc: "Choose a scenario and enter your numbers. We'll simulate the outcomes safely.",
      diffScenario: 'Choose different scenario',
      yourMetrics: 'YOUR METRICS',
      aiModel: 'AI MODEL',
      outcomes: 'OUTCOMES',
      simulation: 'Simulation',
      scenarios: '3 scenarios',
      context: 'Additional context (optional)',
      contextPlaceholder: "e.g., We're in a competitive market with price-sensitive customers",
      runBtn: 'Run Simulation',
      simulating: 'Simulating scenario...',
      simDesc: 'Building business model and calculating outcomes',
      bestCase: 'Best Case', realistic: 'Realistic', worstCase: 'Worst Case',
      revenue: 'Revenue', profit: 'Profit',
      timeline: 'Implementation Timeline',
      month: 'Month',
      showAnalysis: 'Show Full AI Analysis',
      hideAnalysis: 'Hide Full AI Analysis',
      chatPlaceholder: "Ask about the simulation... (e.g., 'What if we combine this with cost reduction?')",
      newSim: 'New Simulation',
      saveBtn: 'Save Simulation',
      savedBtn: 'Saved Simulations',
      compareBtn: 'Compare',
      saveTitle: 'Simulation title',
      saveNotes: 'Notes (optional)',
      cancel: 'Cancel',
      saveSave: 'Save',
      savedOk: 'Saved!',
      noSaved: 'No saved simulations yet — run one and save it to revisit later.',
      selectCompare: 'Select 2 or more simulations to compare',
      compareNow: 'Compare Selected',
      backToSaved: 'Back to saved',
      comparing: 'Comparing simulations...',
      recommendation: 'Recommendation', riskScore: 'Risk Score', confidence: 'Confidence',
      inputs: 'inputs',
      followups: [
        "What if we do this gradually over 12 months?",
        "What are the biggest risks to watch for?",
        "Compare this with doing nothing",
      ],
    },
    nl: {
      pageTitle: 'AI Beslissing Simulator',
      pageSubtitle: 'Test zakelijke beslissingen veilig voordat je handelt',
      quickQuestion: 'Snelle Beslissing',
      quickQuestionDesc: 'Stel direct je vraag - geen cijfers nodig',
      quickPlaceholder: 'Bijv: Moet ik een medewerker aannemen? Moet ik mijn prijzen 10% verhogen?',
      askBtn: 'Krijg AI Advies',
      orChoose: '— of kies een gedetailleerd scenario —',
      chooseScenario: 'Welke zakelijke beslissing wil je testen?',
      chooseDesc: 'Kies een scenario en vul je cijfers in. We simuleren de uitkomsten veilig.',
      diffScenario: 'Kies ander scenario',
      yourMetrics: 'JOUW CIJFERS',
      aiModel: 'AI MODEL',
      outcomes: 'UITKOMSTEN',
      simulation: 'Simulatie',
      scenarios: '3 scenarios',
      context: 'Extra context (optioneel)',
      contextPlaceholder: 'bijv. We zitten in een competitieve markt met prijsgevoelige klanten',
      runBtn: 'Simulatie Starten',
      simulating: 'Scenario simuleren...',
      simDesc: 'Bedrijfsmodel bouwen en uitkomsten berekenen',
      bestCase: 'Best Case', realistic: 'Realistisch', worstCase: 'Worst Case',
      revenue: 'Omzet', profit: 'Winst',
      timeline: 'Implementatie Tijdlijn',
      month: 'Maand',
      showAnalysis: 'Toon Volledige AI Analyse',
      hideAnalysis: 'Verberg AI Analyse',
      chatPlaceholder: 'Vraag over de simulatie... (bijv. "Wat als we dit combineren met kostenreductie?")',
      newSim: 'Nieuwe Simulatie',
      saveBtn: 'Simulatie Opslaan',
      savedBtn: 'Opgeslagen Simulaties',
      compareBtn: 'Vergelijken',
      saveTitle: 'Simulatie titel',
      saveNotes: 'Notities (optioneel)',
      cancel: 'Annuleren',
      saveSave: 'Opslaan',
      savedOk: 'Opgeslagen!',
      noSaved: 'Nog geen opgeslagen simulaties — voer er één uit en bewaar hem voor later.',
      selectCompare: 'Selecteer 2 of meer simulaties om te vergelijken',
      compareNow: 'Vergelijk Selectie',
      backToSaved: 'Terug naar opgeslagen',
      comparing: 'Simulaties vergelijken...',
      recommendation: 'Aanbeveling', riskScore: 'Risicoscore', confidence: 'Betrouwbaarheid',
      inputs: 'invoerwaarden',
      followups: [
        "Wat als we dit geleidelijk doen over 12 maanden?",
        "Wat zijn de grootste risicos om in de gaten te houden?",
        "Vergelijk dit met niets doen",
      ],
    },
  };
  const l = txt[lang] || txt.en;

  useEffect(() => { loadPresets(); loadSaved(); }, []);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadPresets = async () => {
    try {
      const res = await axios.get(`${API}/compass/simulator/presets`);
      setPresets(res.data.presets);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadSaved = async () => {
    try {
      const res = await axios.get(`${API}/compass/simulator/saved`, { headers });
      setSavedSims(res.data.simulations || []);
    } catch (e) { console.error(e); }
  };

  const selectPreset = (preset) => { setSelectedPreset(preset); setMetrics({ ...preset.default_metrics }); };
  const updateMetric = (key, value) => { setMetrics(prev => ({ ...prev, [key]: parseFloat(value) || 0 })); };
  const formatMetricLabel = (key) => ((key) == null ? '' : String(key)).replace(/_/g, ' ').replace(/\b\w/g, c => ((c) == null ? '' : String(c)).toUpperCase());

  const runSimulation = async () => {
    if (!selectedPreset) return;
    setSimulating(true); setView('result');
    try {
      const res = await axios.post(`${API}/compass/simulator/simulate`, {
        scenario: selectedPreset.title, metrics, custom_question: customQuestion || null, language: lang,
      }, { headers });
      setSessionId(res.data.session_id);
      setSimData(res.data.simulation_data);
      setAnalysisText(res.data.analysis);
      setMessages([{ role: 'assistant', content: res.data.analysis }]);
    } catch (e) { setAnalysisText('Simulation failed.'); }
    finally { setSimulating(false); }
  };

  const runQuickDecision = async () => {
    if (!((quickQuestion) == null ? '' : String(quickQuestion)).trim()) return;
    setSimulating(true); setView('result');
    try {
      const res = await axios.post(`${API}/compass/simulator/quick-decision`, {
        question: quickQuestion, language: lang,
      }, { headers });
      setSessionId(res.data.session_id);
      setAnalysisText(res.data.analysis);
      setMessages([
        { role: 'user', content: quickQuestion },
        { role: 'assistant', content: res.data.analysis }
      ]);
    } catch (e) { setAnalysisText('Analysis failed.'); }
    finally { setSimulating(false); }
  };

  const sendChat = async (text) => {
    if (!((text) == null ? '' : String(text)).trim() || sending || !sessionId) return;
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setChatInput(''); setSending(true);
    try {
      const res = await axios.post(`${API}/compass/simulator/chat`, { message: text, session_id: sessionId, language: lang }, { headers });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (e) { setMessages(prev => [...prev, { role: 'assistant', content: lang === 'en' ? 'Error. Please try again.' : 'Fout. Probeer het opnieuw.' }]); }
    finally { setSending(false); }
  };

  const startNew = () => {
    setView('setup'); setSelectedPreset(null); setMetrics({}); setCustomQuestion('');
    setSimData(null); setAnalysisText(''); setSessionId(null); setMessages([]);
    setShowFullAnalysis(false); setSavedOk(false); setQuickQuestion('');
  };

  const saveSimulation = async () => {
    if (!((saveTitle) == null ? '' : String(saveTitle)).trim() || !sessionId) return;
    setSaving(true);
    try {
      await axios.post(`${API}/compass/simulator/save`, { session_id: sessionId, title: saveTitle, notes: saveNotes }, { headers });
      setSavedOk(true); setShowSaveModal(false); setSaveTitle(''); setSaveNotes('');
      loadSaved();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const deleteSaved = async (id) => {
    try {
      await axios.delete(`${API}/compass/simulator/saved/${id}`, { headers });
      setSavedSims(prev => (Array.isArray(prev) ? prev : []).filter(s => s.save_id !== id));
      setCompareSelected(prev => (Array.isArray(prev) ? prev : []).filter(i => i !== id));
    } catch (e) { console.error(e); }
  };

  const toggleCompareSelect = (id) => {
    setCompareSelected(prev => (Array.isArray(prev) ? prev : []).includes(id) ? (Array.isArray(prev) ? prev : []).filter(i => i !== id) : [...prev, id]);
  };

  const runCompare = async () => {
    if (compareSelected.length < 2) return;
    setComparing(true); setCompareResult(null);
    try {
      const res = await axios.post(`${API}/compass/simulator/compare`, { simulation_ids: compareSelected }, { headers });
      setCompareResult(res.data);
      setView('compare');
    } catch (e) { console.error(e); }
    finally { setComparing(false); }
  };

  if (loading) return <div className="loading-screen"><div className="loader"></div></div>;

  // ─── SAVED VIEW ───
  if (view === 'saved') {
    return (
      <div className="tool-page">
        <div className="tool-header">
          <button onClick={() => setView('setup')} className="btn-secondary"><ArrowLeft size={16} /> {l.newSim}</button>
          <h1 className="heading-1"><BookOpen size={22} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} /> {l.savedBtn}</h1>
        </div>
        <div className="tool-content">
          {savedSims.length === 0 ? (
            <p className="body-medium text-secondary" style={{ textAlign: 'center', marginTop: '3rem' }}>{l.noSaved}</p>
          ) : (
            <>
              <p className="body-small text-secondary" style={{ marginBottom: '1rem' }}>{l.selectCompare}</p>
              <div className="saved-sims-grid" data-testid="saved-sims-grid">
                {(Array.isArray(savedSims) ? savedSims : []).map((sim) => (
                  <div key={sim.save_id} className={`saved-sim-card ${(Array.isArray(compareSelected) ? compareSelected : []).includes(sim.save_id) ? 'selected' : ''}`} data-testid={`saved-sim-${sim.save_id}`}>
                    <div className="saved-sim-header">
                      <input type="checkbox" checked={(Array.isArray(compareSelected) ? compareSelected : []).includes(sim.save_id)} onChange={() => toggleCompareSelect(sim.save_id)} data-testid={`compare-check-${sim.save_id}`} />
                      <h3 className="heading-3">{sim.title}</h3>
                      <button onClick={() => deleteSaved(sim.save_id)} className="btn-icon-sm" data-testid={`delete-sim-${sim.save_id}`}><Trash2 size={14} /></button>
                    </div>
                    <p className="body-small text-secondary">{sim.scenario}</p>
                    {sim.notes && <p className="caption text-secondary" style={{ fontStyle: 'italic' }}>{sim.notes}</p>}
                    <p className="caption text-secondary">{new Date(sim.saved_at).toLocaleDateString(lang === 'nl' ? 'nl-NL' : 'en-US')}</p>
                    <ExportPDFButton title={sim.title} content={`Scenario: ${sim.scenario}\n\nMetrics: ${JSON.stringify(sim.metrics)}\n\nResults:\n${JSON.stringify(sim.simulation_data, null, 2)}`} toolName="Scenario Simulator" />
                  </div>
                ))}
              </div>
              {compareSelected.length >= 2 && (
                <button className="btn-primary" style={{ marginTop: '1rem' }} onClick={runCompare} disabled={comparing} data-testid="compare-btn">
                  {comparing ? <><Loader size={14} className="spin" /> {l.comparing}</> : <><GitCompare size={16} /> {l.compareNow} ({compareSelected.length})</>}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // ─── COMPARE VIEW ───
  if (view === 'compare' && compareResult) {
    return (
      <div className="tool-page">
        <div className="tool-header">
          <button onClick={() => setView('saved')} className="btn-secondary"><ArrowLeft size={16} /> {l.backToSaved}</button>
          <h1 className="heading-1"><GitCompare size={22} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} /> {l.compareBtn}</h1>
        </div>
        <div className="tool-content">
          {/* Side by side cards */}
          <div className="compare-cards" data-testid="compare-cards">
            {(Array.isArray(compareResult.simulations) ? compareResult.simulations : []).map((sim, i) => (
              <div key={sim.save_id} className="compare-card" data-testid={`compare-card-${i}`}>
                <h3 className="heading-3">{sim.title}</h3>
                <p className="body-small text-secondary">{sim.scenario}</p>
                <div className="compare-metrics">
                  {sim.metrics && Object.entries(sim.metrics).map(([k, v]) => (
                    <div key={k} className="compare-metric"><span className="caption">{formatMetricLabel(k)}</span><span className="body-small" style={{ fontWeight: 600 }}>{v}</span></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {/* AI Comparison */}
          <div className="compare-analysis markdown-content" data-testid="compare-analysis">
            <ReactMarkdown>{compareResult.comparison}</ReactMarkdown>
          </div>
          <ExportPDFButton title={`Vergelijking: ${(Array.isArray(compareResult.simulations) ? compareResult.simulations : []).map(s => s.title).join(' vs ')}`} content={compareResult.comparison} toolName="Scenario Simulator" />
        </div>
      </div>
    );
  }

  // ─── SETUP VIEW ───
  if (view === 'setup') {
    return (
      <div className="tool-page">
        <div className="tool-header">
          <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary"><ArrowLeft size={16} /> {t.common.aiTools}</button>
          <h1 className="heading-1"><FlaskConical size={22} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} /> {l.pageTitle}</h1>
          <button onClick={() => { setView('saved'); loadSaved(); }} className="btn-secondary" data-testid="goto-saved"><BookOpen size={16} /> {l.savedBtn} {savedSims.length > 0 && `(${savedSims.length})`}</button>
        </div>
        <div className="tool-content">
          <p className="body-medium text-secondary" style={{ marginBottom: '2rem', maxWidth: '600px' }}>{l.pageSubtitle}</p>
          
          <div className="sim-setup" data-testid="simulator-setup">
            {/* Quick Decision Section */}
            <div className="auto-input-card" style={{ marginBottom: '2rem' }} data-testid="quick-decision">
              <div className="auto-input-header">
                <Lightbulb size={20} />
                <h3 className="heading-3">{l.quickQuestion}</h3>
              </div>
              <p className="body-small text-secondary">{l.quickQuestionDesc}</p>
              <textarea
                className="form-input auto-textarea"
                placeholder={l.quickPlaceholder}
                value={quickQuestion}
                onChange={(e) => setQuickQuestion(e.target.value)}
                rows={3}
                style={{ marginTop: '1rem' }}
                data-testid="quick-question-input"
              />
              <button 
                className="btn-primary" 
                style={{ width: '100%', marginTop: '1rem' }}
                onClick={runQuickDecision}
                disabled={!((quickQuestion) == null ? '' : String(quickQuestion)).trim() || simulating}
                data-testid="quick-decision-btn"
              >
                {simulating ? <Loader className="spin" size={16} /> : <Target size={16} />}
                {l.askBtn}
              </button>
            </div>
            
            <div style={{ textAlign: 'center', margin: '1.5rem 0', color: 'var(--text-secondary)' }}>{l.orChoose}</div>
            
            {!selectedPreset ? (
              <>
                <div className="sim-intro">
                  <h2 className="heading-2" style={{ textAlign: 'center' }}>{l.chooseScenario}</h2>
                  <p className="body-medium text-secondary" style={{ textAlign: 'center', marginBottom: '2rem' }}>{l.chooseDesc}</p>
                </div>
                <div className="sim-preset-grid" data-testid="preset-grid">
                  {(Array.isArray(presets) ? presets : []).map(p => {
                    const Icon = PRESET_ICONS[p.icon] || FlaskConical;
                    return (
                      <button key={p.id} className="sim-preset-card" onClick={() => selectPreset(p)} data-testid={`preset-${p.id}`}>
                        <div className="sim-preset-icon"><Icon size={24} /></div>
                        <h3 className="heading-3">{lang === 'nl' && p.title ? p.title : p.title_en || p.title}</h3>
                        <p className="body-small text-secondary">{p.desc}</p>
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="sim-config" data-testid="sim-config">
                <button className="btn-secondary" onClick={() => setSelectedPreset(null)} style={{ marginBottom: '1rem' }}><ArrowLeft size={14} /> {l.diffScenario}</button>
                <div className="sim-config-card">
                  <h2 className="heading-2">{lang === 'nl' && selectedPreset.title ? selectedPreset.title : selectedPreset.title_en || selectedPreset.title}</h2>
                  <p className="body-medium text-secondary" style={{ marginBottom: '1.5rem' }}>{selectedPreset.desc}</p>
                  
                  {/* Suggested questions for this preset */}
                  {selectedPreset.questions && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <span className="caption" style={{ fontWeight: 600 }}>{lang === 'nl' ? 'VOORBEELDVRAGEN' : 'EXAMPLE QUESTIONS'}</span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                        {selectedPreset.questions.slice(0, 3).map((q, i) => (
                          <button key={i} className="compass-suggestion-btn" onClick={() => setCustomQuestion(q)} style={{ fontSize: '0.75rem' }}>
                            {q}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="sim-flow" data-testid="sim-flow">
                    <div className="sim-flow-node sim-flow-input"><span className="caption">{l.yourMetrics}</span><span className="body-small" style={{ fontWeight: 600 }}>{Object.keys(metrics).length} {l.inputs}</span></div>
                    <ArrowRight size={20} className="sim-flow-arrow" />
                    <div className="sim-flow-node sim-flow-model"><span className="caption">{l.aiModel}</span><span className="body-small" style={{ fontWeight: 600 }}>{l.simulation}</span></div>
                    <ArrowRight size={20} className="sim-flow-arrow" />
                    <div className="sim-flow-node sim-flow-output"><span className="caption">{l.outcomes}</span><span className="body-small" style={{ fontWeight: 600 }}>{l.scenarios}</span></div>
                  </div>
                  <div className="sim-metrics-grid" data-testid="metrics-grid">
                    {Object.entries(metrics).map(([key, val]) => (
                      <div key={key} className="sim-metric-input">
                        <label className="body-small" style={{ fontWeight: 500 }}>{formatMetricLabel(key)}</label>
                        <input type="number" className="form-input" value={val} onChange={(e) => updateMetric(key, e.target.value)} data-testid={`metric-${key}`} />
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: '1.25rem' }}>
                    <label className="body-small" style={{ fontWeight: 500, display: 'block', marginBottom: '0.5rem' }}>{l.context}</label>
                    <input type="text" className="form-input" placeholder={l.contextPlaceholder} value={customQuestion} onChange={(e) => setCustomQuestion(e.target.value)} data-testid="custom-context" />
                  </div>
                  <button className="btn-primary" style={{ width: '100%', marginTop: '1.5rem' }} onClick={runSimulation} data-testid="run-simulation-btn"><FlaskConical size={16} /> {l.runBtn}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── RESULT VIEW ───
  return (
    <div className="compass-coach-page" data-testid="simulator-result">
      <aside className="compass-sidebar">
        <div className="compass-sidebar-header">
          <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary" style={{ width: '100%', justifyContent: 'center' }}><ArrowLeft size={14} /> {t.common.aiTools}</button>
        </div>
        <div className="compass-role-badge" style={{ background: 'var(--accent-green-200)' }}>
          <FlaskConical size={16} />
          <span className="body-small" style={{ fontWeight: 500 }}>{selectedPreset?.title || l.simulation}</span>
        </div>
        {simData && (
          <div className="sim-sidebar-stats" data-testid="sim-quick-stats">
            <div className="sim-stat-item"><Target size={14} /><span className="body-small">{l.recommendation}</span>
              <span className={`sim-rec-badge ${simData.recommendation?.toLowerCase().includes('go') ? 'sim-rec-go' : 'sim-rec-caution'}`}>{simData.recommendation || 'N/A'}</span></div>
            <div className="sim-stat-item"><Shield size={14} /><span className="body-small">{l.riskScore}</span><span className="body-small" style={{ fontWeight: 600 }}>{simData.risk_score || 'N/A'}/10</span></div>
            <div className="sim-stat-item"><Lightbulb size={14} /><span className="body-small">{l.confidence}</span><span className="body-small" style={{ fontWeight: 600 }}>{simData.confidence || 'N/A'}</span></div>
          </div>
        )}
        {/* Save button */}
        {sessionId && !savedOk && (
          <button className="compass-new-chat" onClick={() => setShowSaveModal(true)} data-testid="save-sim-btn" style={{ background: 'var(--accent-green-200)' }}>
            <Save size={14} /> {l.saveBtn}
          </button>
        )}
        {savedOk && (
          <div className="compass-new-chat" style={{ background: 'var(--accent-green-200)', cursor: 'default', justifyContent: 'center' }}><Check size={14} /> {l.savedOk}</div>
        )}
        <ExportPDFButton title={selectedPreset?.title || 'Simulation'} content={analysisText} toolName="Scenario Simulator" className="compass-new-chat" />
        <button className="compass-new-chat" onClick={startNew} data-testid="new-sim-btn"><Plus size={14} /> {l.newSim}</button>
        <button className="compass-new-chat" onClick={() => { setView('saved'); loadSaved(); }} data-testid="goto-saved-result"><BookOpen size={14} /> {l.savedBtn}</button>
      </aside>

      {/* Save Modal */}
      {showSaveModal && (
        <div className="modal-overlay" onClick={() => setShowSaveModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} data-testid="save-modal">
            <h3 className="heading-3">{l.saveBtn}</h3>
            <input type="text" className="form-input" placeholder={l.saveTitle} value={saveTitle} onChange={e => setSaveTitle(e.target.value)} data-testid="save-title-input" autoFocus />
            <textarea className="form-input" placeholder={l.saveNotes} value={saveNotes} onChange={e => setSaveNotes(e.target.value)} rows={3} style={{ marginTop: '0.5rem' }} data-testid="save-notes-input" />
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <button className="btn-secondary" onClick={() => setShowSaveModal(false)} style={{ flex: 1 }}>{l.cancel}</button>
              <button className="btn-primary" onClick={saveSimulation} disabled={!((saveTitle) == null ? '' : String(saveTitle)).trim() || saving} style={{ flex: 1 }} data-testid="save-confirm-btn">
                {saving ? <Loader size={14} className="spin" /> : <Save size={14} />} {l.saveSave}
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="compass-main">
        {simulating && !simData ? (
          <div className="auto-analyzing-state">
            <Loader className="spin" size={32} />
            <h3 className="heading-3">{l.simulating}</h3>
            <p className="body-small text-secondary">{l.simDesc}</p>
          </div>
        ) : (
          <div className="compass-chat-container">
            {simData?.outcomes && (
              <div className="sim-outcomes" data-testid="outcome-cards">
                {['best_case', 'realistic', 'worst_case'].map((key) => {
                  const o = simData.outcomes[key];
                  if (!o) return null;
                  const labels = { best_case: l.bestCase, realistic: l.realistic, worst_case: l.worstCase };
                  const clrs = { best_case: 'var(--accent-green-200)', realistic: 'var(--accent-blue-200)', worst_case: 'var(--accent-pink-200)' };
                  return (
                    <div key={key} className="sim-outcome-card" style={{ borderTopColor: clrs[key] }} data-testid={`outcome-${key}`}>
                      <span className="caption">{labels[key]}</span>
                      <div className="sim-outcome-metrics">
                        <span className="body-small"><strong>{l.revenue}:</strong> {o.revenue_impact}</span>
                        <span className="body-small"><strong>{l.profit}:</strong> {o.profit_impact}</span>
                      </div>
                      <p className="caption text-secondary">{o.description}</p>
                    </div>
                  );
                })}
              </div>
            )}
            {simData?.timeline && simData.timeline.length > 0 && (
              <div className="sim-timeline" data-testid="sim-timeline">
                <h4 className="body-small" style={{ fontWeight: 600, marginBottom: '0.75rem' }}>{l.timeline}</h4>
                <div className="sim-timeline-steps">
                  {(Array.isArray(simData.timeline) ? simData.timeline : []).map((step, i) => (
                    <div key={i} className="sim-timeline-step"><div className="sim-timeline-dot" /><div>
                      <span className="body-small" style={{ fontWeight: 600 }}>{l.month} {step.month}: {step.event}</span>
                      <span className="caption text-secondary">{step.impact}</span>
                    </div></div>
                  ))}
                </div>
              </div>
            )}
            {analysisText && (
              <div className="sim-full-analysis" data-testid="full-analysis">
                <button className="sim-analysis-toggle" onClick={() => setShowFullAnalysis(!showFullAnalysis)}>
                  <MessageSquare size={14} />
                  <span className="body-small" style={{ fontWeight: 500 }}>{showFullAnalysis ? l.hideAnalysis : l.showAnalysis}</span>
                  {showFullAnalysis ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
                {showFullAnalysis && <div className="sim-analysis-content markdown-content"><ReactMarkdown>{analysisText}</ReactMarkdown></div>}
              </div>
            )}
            <div className="compass-chat-messages" style={{ flex: 'none', maxHeight: '400px' }} data-testid="sim-chat">
              {messages.slice(1).map((msg, i) => (
                <div key={i} className={`compass-msg compass-msg-${msg.role}`}>
                  {msg.role === 'assistant' ? <div className="compass-msg-content markdown-content"><ReactMarkdown>{msg.content}</ReactMarkdown></div>
                    : <div className="compass-msg-content">{msg.content}</div>}
                </div>
              ))}
              {sending && <div className="compass-msg compass-msg-assistant"><div className="compass-msg-content compass-typing"><Loader className="spin" size={16} /></div></div>}
              <div ref={chatEndRef} />
            </div>
            {!sending && messages.length <= 2 && (
              <div className="auto-followup-bar" data-testid="sim-followups">
                {(Array.isArray(l.followups) ? l.followups : []).map((s, i) => (
                  <button key={i} className="compass-suggestion-btn" onClick={() => sendChat(s)} data-testid={`sim-followup-${i}`}><Lightbulb size={14} /> {s}</button>
                ))}
              </div>
            )}
            <form className="compass-chat-input" onSubmit={(e) => { e.preventDefault(); sendChat(chatInput); }} data-testid="sim-chat-form">
              <input type="text" value={chatInput} onChange={(e) => setChatInput(e.target.value)} placeholder={l.chatPlaceholder} disabled={sending || simulating} className="form-input" data-testid="sim-chat-input" />
              <button type="submit" disabled={sending || !((chatInput) == null ? '' : String(chatInput)).trim()} className="btn-primary compass-send-btn" data-testid="sim-send-btn"><Send size={16} /></button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
};

export default ScenarioSimulator;
